import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BackLink, Button, ErrorNotice, PageEyebrow, SegmentedControl, SelectInput, TextInput } from '@bcecloud/portal-ui';
import { getJson, sendJson, errorMessage } from '@/lib/api';
import { slugify } from '@/lib/slug';
import { ORGANIZATION_KINDS, PARTNER_TYPE_LABELS, type OrganizationKind, type OrganizationDetail, type PartnerType, type SlugCheck } from '@/lib/types';

/** Debounce for the live slug check, so typing does not hammer the API. */
const SLUG_CHECK_DELAY_MS = 350;

/**
 * Creates a client organization.
 *
 * The slug is the load-bearing field — it becomes the client's subdomain and
 * cannot be changed casually afterwards — so it is checked against the server
 * as the admin types, showing the resulting URL and any reason it is refused.
 *
 * @returns The create-organization form.
 */
export function NewOrganizationPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [kind, setKind] = useState<OrganizationKind>(
    params.get('kind') === 'partner' ? ORGANIZATION_KINDS.partner : ORGANIZATION_KINDS.client,
  );
  const partner = kind === ORGANIZATION_KINDS.partner;
  const [partnerType, setPartnerType] = useState<PartnerType>('HOTEL');
  // A partner's showcase uses a main color and a button color; a client portal a main and a sidebar color.
  const [accentColor, setAccentColor] = useState('#ffc72c');

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#00338d');
  const [sidebarColor, setSidebarColor] = useState('#001d52');
  const [debouncedSlug, setDebouncedSlug] = useState('');

  // Suggest the slug from the name until the admin takes it over, then leave
  // theirs alone — retyping the name should not silently undo their choice.
  useEffect(() => {
    if (!slugEdited) setSlug(slugify(name));
  }, [name, slugEdited]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSlug(slug), SLUG_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [slug]);

  // The wildcard base the address hangs off. From the API rather than
  // hardcoded, so the form shows .localtest.me locally and the real domain in
  // production without a rebuild.
  const publicConfig = useQuery({
    queryKey: ['public-config'],
    queryFn: async ({ signal }) => {
      const response = await fetch('/api/v3/portal/public/config', signal ? { signal } : {});
      const body = (await response.json()) as {
        data?: { portalBaseDomain?: string };
      };
      return body.data ?? {};
    },
    staleTime: Infinity,
  });
  const baseDomain = publicConfig.data?.portalBaseDomain;

  const slugCheck = useQuery({
    queryKey: ['slug-check', debouncedSlug],
    queryFn: ({ signal }) =>
      getJson<SlugCheck>('/organizations/slug-check', { slug: debouncedSlug }, signal),
    enabled: debouncedSlug.length >= 2,
  });

  const create = useMutation({
    mutationFn: () =>
      sendJson<OrganizationDetail>('post', '/organizations', {
        name: name.trim(),
        slug,
        ...(partner
          ? { kind, partnerType, brand: { primaryColor, sidebarColor, accentColor } }
          : { brand: { primaryColor, sidebarColor } }),
      }),
    onSuccess: async (organization) => {
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      navigate(`/organizations/${organization.id}`);
    },
  });

  const slugAvailable = slugCheck.data?.available === true;
  const canSubmit = name.trim().length > 0 && slug.length >= 2 && slugAvailable && !create.isPending;

  return (
    <section className="max-w-2xl animate-portal-rise">
      <BackLink to="/organizations">All organizations</BackLink>

      <div className="mt-4">
        <PageEyebrow>CLIENT PORTALS</PageEyebrow>
      </div>
      <h1 className="mb-2 text-portal-page tracking-[-1.7px]">{partner ? 'New partner' : 'New client portal'}</h1>
      <p className="text-portal-small text-portal-muted">
        {partner
          ? 'Creates the Clerk organization, the partner’s portal and their program showcase. Invite their contact from the Members tab next.'
          : 'Creates the Clerk organization, the branded portal and a central credit pool.'}
      </p>

      <div className="mt-6">
        <SegmentedControl
          guide="new-organization-kind"
          label="This portal is for"
          options={[
            { key: ORGANIZATION_KINDS.client, label: 'A client' },
            { key: ORGANIZATION_KINDS.partner, label: 'A showcase partner' },
          ]}
          value={kind}
          onChange={setKind}
        />
      </div>

      <form
        className="mt-8 space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <div data-guide="new-organization-name">
          <label htmlFor="name" className="block text-portal-tiny font-bold">
            {partner ? 'Partner name' : 'Client name'}
          </label>
          <TextInput
            id="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoComplete="organization"
            placeholder={partner ? 'Harborview Hotel' : 'Northwind Group'}
          />
        </div>

        {partner ? (
          <div>
            <label htmlFor="partnerType" className="block text-portal-tiny font-bold">
              Partner type
            </label>
            <SelectInput id="partnerType" value={partnerType} onChange={(event) => setPartnerType(event.target.value as PartnerType)}>
              {(Object.keys(PARTNER_TYPE_LABELS) as PartnerType[]).map((type) => (
                <option key={type} value={type}>
                  {PARTNER_TYPE_LABELS[type]}
                </option>
              ))}
            </SelectInput>
            <p className="mt-2 text-sm text-admin-muted">
              Sets the Lead Source on their leads: Referral - Venue for hotels and venues, Referral - Charity for nonprofits.
            </p>
          </div>
        ) : null}

        <div data-guide="new-organization-address">
          <label htmlFor="slug" className="block text-portal-tiny font-bold">
            Portal address
          </label>
          {/*
            An input group rather than a bare field: the suffix is fixed, so
            showing it beside what the admin types means the full address is
            visible at all times — what you see is exactly what the client gets.
          */}
          <div className="mt-2 flex">
            <input
              id="slug"
              value={slug}
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(slugify(event.target.value));
              }}
              required
              placeholder="northwind"
              aria-describedby="slug-status slug-suffix"
              className="h-11 w-full min-w-0 rounded-l-md border border-admin-line bg-white px-3 font-mono text-sm"
            />
            <span
              id="slug-suffix"
              className="flex h-11 shrink-0 items-center rounded-r-md border border-l-0 border-admin-line bg-portal-bg px-3 font-mono text-sm text-admin-muted"
            >
              .{baseDomain ?? '…'}
            </span>
          </div>

          <p id="slug-status" aria-live="polite" className="mt-2 text-sm">
            {debouncedSlug.length < 2 ? (
              <span className="text-admin-muted">
                The client's portal lives at this address. Short and lowercase — it becomes part
                of the URL and cannot be changed casually later.
              </span>
            ) : slugCheck.isPending ? (
              <span className="text-admin-muted">Checking availability…</span>
            ) : slugCheck.data?.available ? (
              <span className="text-emerald-700">
                Available — <span className="font-mono">{slugCheck.data.portalUrl}</span>
              </span>
            ) : (
              <span className="text-destructive">{slugCheck.data?.message ?? 'Unavailable.'}</span>
            )}
          </p>
        </div>

        <fieldset data-guide="new-organization-colors">
          <legend className="text-sm font-semibold">Brand colors</legend>
          <p className="mt-1 text-sm text-admin-muted">
            {partner
              ? 'Starting point only. The partner can change their colors and add a logo from their portal.'
              : 'Starting point only — the full brand editor opens once the client is created.'}
          </p>

          <div className="mt-3 flex gap-6">
            <ColorField id="primaryColor" label="Primary" value={primaryColor} onChange={setPrimaryColor} />
            {partner ? (
              <ColorField id="accentColor" label="Buttons" value={accentColor} onChange={setAccentColor} />
            ) : (
              <ColorField id="sidebarColor" label="Sidebar" value={sidebarColor} onChange={setSidebarColor} />
            )}
          </div>
        </fieldset>

        {create.error ? (
          <ErrorNotice inline title={partner ? 'Could not create partner' : 'Could not create portal'} message={errorMessage(create.error)} />
        ) : null}

        <Button guide="new-organization-create" type="submit" variant="primary" disabled={!canSubmit}>
          {create.isPending ? 'Creating…' : partner ? 'Create partner' : 'Create client portal'}
        </Button>
      </form>
    </section>
  );
}

interface ColorFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * A color swatch paired with its hex value.
 *
 * Both controls are editable: the picker for choosing, the text field for
 * pasting an exact value out of a brand guideline, which is how these actually
 * arrive.
 *
 * @param props - Field identity, label and value.
 * @returns The color field.
 */
function ColorField({ id, label, value, onChange }: ColorFieldProps): React.ReactElement {
  return (
    <div>
      <label htmlFor={id} className="block text-portal-tiny font-bold">
        {label}
      </label>
      <div className="mt-2 flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="size-10 cursor-pointer rounded-md border border-admin-line bg-white p-1"
        />
        <input
          aria-label={`${label} hex value`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-28 rounded-md border border-admin-line bg-white px-2 font-mono text-sm"
        />
      </div>
    </div>
  );
}
